import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import Loader from "../components/Loader";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faUserGroup,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";
import isEqual from "lodash/isEqual";
import { CloseIcon, ModernButton } from "./DefaultImports";
import { CustomModal } from "../components/CustomModal";
import zIndex from "@mui/material/styles/zIndex";

function cleanList(list) {
  return list.map((sublist) => {
    return sublist.map((item) => {
      const splitItem = item.split(";");
      return { number: splitItem[0], status: splitItem[1] };
    });
  });
}
const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(33, 33, 33, 0.5);
  z-index: 2000;
`;

const StyledTableDiv = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bbb;
  border-radius: 6px;
  padding: 6px;
  margin: 6px;
  font-weight: 500;
  font-size: 14px;
  text-align: center;

  cursor: pointer;
  transition: all 0.2s ease-in-out;

  background-color: ${({ isSelected, tableStatus }) =>
    isSelected
      ? tableStatus === "1"
        ? "#EE561B"
        : "#f79574"
      : tableStatus === "1"
        ? "#99B0AD"
        : "#FFFFFF"}; // livre

  color: ${({ isSelected, tableStatus }) =>
    isSelected || tableStatus === "1" ? "white" : "#4B3335"};

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border-color: #ee754c;
  }
`;

const StyledH6 = styled.h6`
  color: ${({ enoughSeats }) => (enoughSeats ? "green" : "red")};
`;

const ZoneContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 15px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background-color: ${({ color }) => color};
  border: 1px solid #bbbbbb; /* ou #333, conforme desejado */
`;

const TablesModal = ({
  show,
  tables_list,
  onClose,
  initialSelectedTables,
  numPeople,
  time,
  date,
  handleTablesUsedChange,
  setSelectedTablesSave,
  setPlaceOfTable,
  placeOfTable,
  setEnoughSeatsReservation,
  reservation,
}) => {
  const tables = cleanList(tables_list);
  const all_tables = useSelector((state) => state.restaurantTables.tables);
  const connectTablesConfig = useSelector(
    (state) => state.connectTablesConfig.connectTablesConfig,
  );
  const [enoughSeats, setEnoughSeats] = useState(false);
  const [countPeople, setCountPeople] = useState(0);
  const [tablesConfigList, setTablesConfigList] = useState([]);
  const [selectedTables, setSelectedTables] = useState({});

  const selectedTablesRef = useRef({});
  useEffect(() => {
    selectedTablesRef.current = selectedTables;
  }, [selectedTables]);

  const lastScenarioRef = useRef("");

  // safe config sum (avoids NaN if config list not ready)
  const configSumFor = (numOfTables) => {
    const cfg = tablesConfigList || [];
    const get = (i) => (Number.isFinite(Number(cfg?.[i])) ? Number(cfg[i]) : 0);

    let configSum = 0;
    if (numOfTables >= 2) configSum += get(0);
    if (numOfTables >= 3) configSum += get(1);
    if (numOfTables >= 4) configSum += get(2);

    for (let i = 5; i <= numOfTables; i++) {
      configSum += get(((i + 1) % 2) + 1);
    }
    return configSum;
  };

  useEffect(() => {
    setSelectedTables(initialSelectedTables || {});
  }, [initialSelectedTables]);

  useEffect(() => {
    // Your requested behavior
    if (show) return;

    if (!reservation || !date || !time || !numPeople) return;
    if (!Array.isArray(tables_list) || tables_list.length === 0) return;
    if (!Array.isArray(all_tables) || all_tables.length === 0) return;

    // Only run when user changed inputs vs reservation
    const changedFromReservation =
      String(reservation?.time) !== String(time) ||
      String(reservation?.date) !== String(date) ||
      Number(reservation?.number_of_people) !== Number(numPeople);

    if (!changedFromReservation) return;

    // Goal is reservation.number_of_people (as you clarified)
    const targetPeople = Number(reservation?.number_of_people) || 0;
    if (targetPeople <= 0) return;

    // Avoid re-running for the same "situation" (prevents loops)
    const availabilityKey = tables_list
      .map((z) => (Array.isArray(z) ? z.join("|") : String(z)))
      .join("||");

    const scenarioKey = `${reservation?.id ?? ""}|${date ?? ""}|${time ?? ""}|${numPeople ?? ""}|${placeOfTable ?? ""}|${availabilityKey}`;
    if (lastScenarioRef.current === scenarioKey) return;

    // Determine active zone (your UI is basically single-zone)
    const activeZone =
      placeOfTable || Object.keys(selectedTablesRef.current || {})[0];

    if (!activeZone) {
      lastScenarioRef.current = scenarioKey;
      return;
    }

    // Find zone row in tables_list
    const zoneRow = tables_list.find(
      (z) => String(z?.[0] ?? "").split(";")[0] === String(activeZone),
    );

    if (!zoneRow) {
      lastScenarioRef.current = scenarioKey;
      return;
    }

    // Seat lookup
    const seatMap = new Map(
      all_tables.map((t) => [
        String(t.id),
        parseInt(t.number_of_seats, 10) || 0,
      ]),
    );

    // Free candidates in this zone (status === "0")
    const freeIds = zoneRow
      .slice(1)
      .map((s) => String(s).split(";"))
      .filter(([, status]) => String(status).trim() === "0")
      .map(([id]) => String(id).trim());

    const candidates = freeIds
      .map((id) => ({ id, seats: seatMap.get(id) || 0 }))
      .filter((c) => c.seats > 0); // ignore weird 0-seat tables

    // If there are no free tables, best solution is empty
    if (candidates.length === 0) {
      lastScenarioRef.current = scenarioKey;
      if (!isEqual(selectedTablesRef.current, {})) setSelectedTables({});
      return;
    }

    // connect-tables bonus (your same logic, but safe)
    const configSumFor = (n) => {
      const cfg = tablesConfigList || [];
      const get = (i) =>
        Number.isFinite(Number(cfg?.[i])) ? Number(cfg[i]) : 0;

      let sum = 0;
      if (n >= 2) sum += get(0);
      if (n >= 3) sum += get(1);
      if (n >= 4) sum += get(2);
      for (let i = 5; i <= n; i++) sum += get(((i + 1) % 2) + 1);
      return sum;
    };

    // We solve: minimize number of tables first, then minimize overshoot.
    // capacity = sumSeats + configSumFor(count)
    const N = candidates.length;
    const maxSum = candidates.reduce((acc, c) => acc + c.seats, 0);

    // dp[count][sum] reachable?
    const dp = Array.from({ length: N + 1 }, () => new Uint8Array(maxSum + 1));
    const parentIdx = Array.from({ length: N + 1 }, () =>
      new Int16Array(maxSum + 1).fill(-1),
    );
    const parentSum = Array.from({ length: N + 1 }, () =>
      new Int32Array(maxSum + 1).fill(-1),
    );

    dp[0][0] = 1;

    for (let i = 0; i < N; i++) {
      const w = candidates[i].seats;
      for (let c = Math.min(i + 1, N); c >= 1; c--) {
        for (let s = maxSum; s >= w; s--) {
          if (dp[c][s]) continue;
          if (!dp[c - 1][s - w]) continue;

          dp[c][s] = 1;
          parentIdx[c][s] = i;
          parentSum[c][s] = s - w;
        }
      }
    }

    // Pick best solution:
    // 1) smallest c that can reach targetPeople
    // 2) within that c, smallest total capacity (=> smallest sumSeats because config is fixed for c)
    let best = null; // { count, sumSeats, cap, reach: true }
    for (let c = 1; c <= N; c++) {
      const reqSeats = Math.max(0, targetPeople - configSumFor(c));

      for (let s = reqSeats; s <= maxSum; s++) {
        if (!dp[c][s]) continue;

        const cap = s + configSumFor(c);
        // First feasible c is minimal tables; first feasible s is minimal seats -> minimal cap
        best = { count: c, sumSeats: s, cap, reach: true };
        break;
      }
      if (best) break;
    }

    // If impossible to reach target, choose closest under target (max cap below target),
    // tie-breaker: fewer tables.
    if (!best) {
      let bestUnder = null; // { count, sumSeats, cap }
      for (let c = 1; c <= N; c++) {
        for (let s = maxSum; s >= 0; s--) {
          if (!dp[c][s]) continue;
          const cap = s + configSumFor(c);
          if (cap >= targetPeople) continue;

          if (
            !bestUnder ||
            cap > bestUnder.cap ||
            (cap === bestUnder.cap && c < bestUnder.count)
          ) {
            bestUnder = { count: c, sumSeats: s, cap };
          }
          break; // for this c, highest s gives highest cap
        }
      }
      if (bestUnder) best = { ...bestUnder, reach: false };
    }

    // Reconstruct chosen IDs
    let chosenIds = [];
    if (best) {
      let c = best.count;
      let s = best.sumSeats;

      while (c > 0) {
        const idx = parentIdx[c][s];
        if (idx < 0) break;
        chosenIds.push(candidates[idx].id);
        s = parentSum[c][s];
        c--;
      }
    }

    chosenIds = [...new Set(chosenIds.map(String))];

    // Build selectedTables result (single zone)
    const nextSelectedTables =
      chosenIds.length > 0
        ? {
            [activeZone]: chosenIds.map((id) => ({
              number: String(id),
              status: "0",
            })),
          }
        : {};

    lastScenarioRef.current = scenarioKey;

    if (!isEqual(nextSelectedTables, selectedTablesRef.current || {})) {
      setSelectedTables(nextSelectedTables);
    }
  }, [
    show,
    date,
    time,
    numPeople,
    placeOfTable,
    tables_list,
    all_tables,
    tablesConfigList,
    reservation?.id,
    reservation?.time,
    reservation?.date,
    reservation?.number_of_people,
  ]);

  useEffect(() => {
    if (connectTablesConfig) {
      const table_config = Object.values(connectTablesConfig).map(
        (config) => config.number_of_chairs,
      );
      setTablesConfigList(table_config);
    }
  }, [connectTablesConfig]);

  useEffect(() => {
    if (all_tables) {
      // 1. Retrieve all the table numbers from selectedTables
      let selectedTableNumbers = [];
      for (let key in selectedTables) {
        selectedTables[key].forEach((table) => {
          selectedTableNumbers.push(table.number);
        });
      }

      if (typeof handleTablesUsedChange === "function") {
        handleTablesUsedChange(selectedTableNumbers);
      }

      // 2. Filter out the tables from all_tables using the retrieved table numbers
      const filteredTables = all_tables.filter((table) =>
        selectedTableNumbers.includes(table.id.toString()),
      );

      // 3. Sum the number of seats of each filtered table
      const totalSeats = filteredTables.reduce(
        (acc, table) => acc + parseInt(table.number_of_seats),
        0,
      );

      // Logic for summing values from tablesConfigList
      let numOfTables = filteredTables.length;
      let configSum = 0;
      if (numOfTables >= 2) configSum += tablesConfigList[0];
      if (numOfTables >= 3) configSum += tablesConfigList[1];
      if (numOfTables >= 4) configSum += tablesConfigList[2];
      for (let i = 5; i <= numOfTables; i++) {
        configSum += tablesConfigList[((i + 1) % 2) + 1];
      }

      if (totalSeats + configSum >= numPeople) {
        setEnoughSeats(true);
      } else {
        setEnoughSeats(false);
      }
      setCountPeople(totalSeats + configSum);
    }
  }, [selectedTables, all_tables]);

  useEffect(() => {
    if (typeof setSelectedTablesSave === "function") {
      setSelectedTablesSave(selectedTables);
    }
  }, [selectedTables, setSelectedTablesSave]);

  const handleTableSelect = (table, zone) => {
    if (placeOfTable !== zone) {
      setPlaceOfTable(zone);
      setSelectedTables({});
    }
    setSelectedTables((prevSelectedTables) => {
      const updatedTables = { ...prevSelectedTables };
      if (updatedTables[zone]?.some((t) => t.number === table.number)) {
        updatedTables[zone] = updatedTables[zone].filter(
          (t) => t.number !== table.number,
        );
      } else {
        updatedTables[zone] = [
          ...(updatedTables[zone] || []),
          { number: table.number, status: table.status },
        ];
      }
      return updatedTables;
    });
  };

  useEffect(() => {
    if (typeof setEnoughSeatsReservation === "function") {
      setEnoughSeatsReservation(enoughSeats);
    }
  }, [enoughSeats, setEnoughSeatsReservation]);

  return (
    <CustomModal
      show={show}
      onHide={onClose}
      title="Atribuir mesas"
      centered={false}
      footer={
        <>
          <LegendContainer>
            <LegendItem>
              <LegendColor color="FFFFFF" /> Livre
            </LegendItem>
            <LegendItem>
              <LegendColor color="#99B0AD" /> Ocupada
            </LegendItem>
            <LegendItem>
              <LegendColor color="#f79574" /> Selecionada
            </LegendItem>
            <LegendItem>
              <LegendColor color="#EE561B" /> Selecionada (mas ocupada)
            </LegendItem>
          </LegendContainer>
          <ModernButton color="#99B0AD" onClick={onClose}>
            Cancelar
          </ModernButton>
          <ModernButton
            color="#de8c3a"
            onClick={onClose}
            style={{ marginLeft: "8px" }}
          >
            Atribuir mesas selecionadas
          </ModernButton>
        </>
      }
    >
      <Row style={{ width: "100%" }}>
        <Col
          className="d-flex justify-content-center"
          xs={12}
          style={{ marginTop: "5px", marginBottom: "10px" }}
        >
          <StyledH6 enoughSeats={enoughSeats}>
            <FontAwesomeIcon
              icon={faUserGroup}
              style={{
                color: enoughSeats ? "green" : "red",
                marginRight: "8px",
              }}
            />
            <strong>{countPeople} lugares</strong> atribuídos para{" "}
            <strong>{numPeople} pessoas</strong>
          </StyledH6>
        </Col>
      </Row>

      {tables && all_tables ? (
        <>
          {tables.map((zoneTables) => (
            <div key={zoneTables[0].number}>
              <h5>{zoneTables[0].number}</h5>
              <ZoneContainer>
                {zoneTables.slice(1).map((table) => {
                  const zoneNumber = zoneTables?.[0]?.number || "defaultKey";
                  const isSelected = selectedTables?.[zoneNumber]?.some(
                    (t) => t.number === table.number,
                  );
                  const the_table = all_tables.find(
                    (t) => t.id.toString() === table.number,
                  );
                  return (
                    <StyledTableDiv
                      key={table.number}
                      isSelected={isSelected}
                      tableStatus={table.status}
                      onClick={() =>
                        handleTableSelect(table, zoneTables[0].number)
                      }
                    >
                      {the_table?.table_name}
                    </StyledTableDiv>
                  );
                })}
              </ZoneContainer>
            </div>
          ))}
        </>
      ) : (
        <Loader />
      )}
    </CustomModal>
  );
};

export default TablesModal;
